import React, { useState, useEffect, useCallback } from "react";
import { I } from "../../Components/CMI/icons";
import { Card, CardHead, CardBody, Badge, Empty } from "../../Components/CMI/primitives";
import { Footer } from "../../Layouts/CMI/PageHead";
import {
  getIvghObjetivos, saveIvghObjetivo, deleteIvghObjetivo,
  getIvghCategorias, saveIvghCategoria,
  getIvghEmpleados, deleteIvghEmpleado,
  getEmpleadosKronaly, vincularEmpleado,
  getUsuariosErp, toggleIvghEmpleado,
} from "../../helpers/ivghApi";

const EMPRESAS = [
  { id: 13, nombre: "Visados Empresas" },
  { id: 14, nombre: "CVS" },
];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

const tabs = [
  { id: "objetivos",  label: "Objetivos",  icon: I.Flag },
  { id: "categorias", label: "Categorías", icon: I.Tag },
  { id: "empleados",  label: "Empleados",  icon: I.Users },
];

/* ── Styles ── */
const S = {
  page: { padding: "32px 24px 64px", maxWidth: 1400, margin: "0 auto" },
  header: { marginBottom: 32 },
  title: { display: "flex", alignItems: "center", gap: 12, margin: 0, fontFamily: "var(--ce-font-display)", fontSize: 22, fontWeight: 700, color: "var(--ce-fg)", letterSpacing: "-0.02em" },
  sub: { marginTop: 6, color: "var(--ce-fg-muted)", fontSize: 13 },
  tabs: { display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid var(--ce-border-subtle)", paddingBottom: 0 },
  tab: (active) => ({
    display: "flex", alignItems: "center", gap: 6, padding: "10px 16px", fontSize: 13, fontWeight: active ? 600 : 400,
    color: active ? "var(--ce-accent)" : "var(--ce-fg-muted)", background: "none", border: "none", cursor: "pointer",
    borderBottom: active ? "2px solid var(--ce-accent)" : "2px solid transparent", marginBottom: -1, transition: "all .15s",
  }),
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "10px 12px", color: "var(--ce-fg-muted)", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", borderBottom: "1px solid var(--ce-border-subtle)" },
  td: { padding: "10px 12px", borderBottom: "1px solid var(--ce-border-subtle)", color: "var(--ce-fg)" },
  row: { display: "flex", gap: 12, marginBottom: 12, flexWrap: "wrap" },
  input: { padding: "8px 12px", fontSize: 13, border: "1px solid var(--ce-border)", borderRadius: 8, background: "var(--ce-bg-card)", color: "var(--ce-fg)", outline: "none", width: "100%" },
  select: { padding: "8px 12px", fontSize: 13, border: "1px solid var(--ce-border)", borderRadius: 8, background: "var(--ce-bg-card)", color: "var(--ce-fg)", outline: "none", width: "100%" },
  btnPrimary: { padding: "8px 18px", fontSize: 13, fontWeight: 600, border: "none", borderRadius: 8, background: "var(--ce-accent)", color: "#fff", cursor: "pointer" },
  btnDanger: { padding: "4px 10px", fontSize: 12, border: "none", borderRadius: 6, background: "var(--ce-bg-danger, #fee)", color: "var(--ce-fg-danger, #c00)", cursor: "pointer" },
  btnGhost: { padding: "8px 18px", fontSize: 13, fontWeight: 500, border: "1px solid var(--ce-border)", borderRadius: 8, background: "none", color: "var(--ce-fg-muted)", cursor: "pointer" },
  btnSuccess: { padding: "4px 12px", fontSize: 12, fontWeight: 600, border: "none", borderRadius: 6, background: "var(--ce-bg-success, #efd)", color: "var(--ce-fg-success, #060)", cursor: "pointer" },
  field: { display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 160 },
  label: { fontSize: 11, fontWeight: 600, color: "var(--ce-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em" },
  actions: { display: "flex", gap: 8, marginTop: 16 },
  msg: (ok) => ({ padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, background: ok ? "var(--ce-bg-success, #efd)" : "var(--ce-bg-danger, #fee)", color: ok ? "var(--ce-fg-success, #060)" : "var(--ce-fg-danger, #c00)" }),
};

const Msg = ({ msg }) => msg ? <div style={S.msg(msg.ok)}>{msg.text}</div> : null;

/* ═══════════════════════════════════════════
   TAB: OBJETIVOS
   ═══════════════════════════════════════════ */
const TabObjetivos = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ empresa_id: "13", ejercicio: currentYear, presupuesto_ventas: "", horas_previstas: "" });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await getIvghObjetivos()); } catch { setRows([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.presupuesto_ventas || !form.horas_previstas) return;
    setMsg(null);
    try {
      await saveIvghObjetivo({
        empresa_id: Number(form.empresa_id),
        ejercicio: Number(form.ejercicio),
        presupuesto_ventas: Number(form.presupuesto_ventas),
        horas_previstas: Number(form.horas_previstas),
      });
      setMsg({ ok: true, text: "Objetivo guardado" });
      setForm({ empresa_id: "13", ejercicio: currentYear, presupuesto_ventas: "", horas_previstas: "" });
      setEditing(null);
      load();
    } catch { setMsg({ ok: false, text: "Error al guardar" }); }
  };

  const handleEdit = (r) => {
    setEditing(r.id);
    setForm({ empresa_id: String(r.empresa_id), ejercicio: r.ejercicio, presupuesto_ventas: r.presupuesto_ventas, horas_previstas: r.horas_previstas });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este objetivo?")) return;
    try { await deleteIvghObjetivo(id); load(); } catch { setMsg({ ok: false, text: "Error al eliminar" }); }
  };

  const ivghBase = form.presupuesto_ventas && form.horas_previstas && Number(form.horas_previstas) > 0
    ? (Number(form.presupuesto_ventas) / Number(form.horas_previstas)).toFixed(2)
    : null;

  return (
    <Card>
      <CardHead icon={I.Flag} title="Objetivos del ejercicio" />
      <CardBody>
        <Msg msg={msg} />
        <div style={S.row}>
          <div style={S.field}>
            <label style={S.label}>Empresa</label>
            <select style={S.select} value={form.empresa_id} onChange={e => setForm({ ...form, empresa_id: e.target.value })}>
              {EMPRESAS.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </select>
          </div>
          <div style={S.field}>
            <label style={S.label}>Ejercicio</label>
            <select style={S.select} value={form.ejercicio} onChange={e => setForm({ ...form, ejercicio: e.target.value })}>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div style={S.field}>
            <label style={S.label}>Presupuesto ventas (€)</label>
            <input style={S.input} type="number" placeholder="1250000" value={form.presupuesto_ventas} onChange={e => setForm({ ...form, presupuesto_ventas: e.target.value })} />
          </div>
          <div style={S.field}>
            <label style={S.label}>Horas previstas</label>
            <input style={S.input} type="number" placeholder="16132" value={form.horas_previstas} onChange={e => setForm({ ...form, horas_previstas: e.target.value })} />
          </div>
        </div>
        {ivghBase && (
          <div style={{ fontSize: 13, color: "var(--ce-fg-muted)", marginBottom: 12 }}>
            IVGH objetivo base: <strong style={{ color: "var(--ce-fg)" }}>{ivghBase} €/h</strong>
          </div>
        )}
        <div style={S.actions}>
          <button style={S.btnPrimary} onClick={handleSave}>{editing ? "Actualizar" : "Guardar"}</button>
          {editing && <button style={S.btnGhost} onClick={() => { setEditing(null); setForm({ empresa_id: "13", ejercicio: currentYear, presupuesto_ventas: "", horas_previstas: "" }); }}>Cancelar</button>}
        </div>

        {loading ? <div style={{ padding: 20, color: "var(--ce-fg-muted)", fontSize: 13 }}>Cargando...</div> : rows.length === 0 ? (
          <Empty icon={I.Flag} title="Sin objetivos" sub="Añade el primer objetivo del ejercicio" />
        ) : (
          <table style={{ ...S.table, marginTop: 24 }}>
            <thead>
              <tr>
                <th style={S.th}>Empresa</th>
                <th style={S.th}>Ejercicio</th>
                <th style={S.th}>Presupuesto</th>
                <th style={S.th}>Horas</th>
                <th style={S.th}>IVGH base</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const empresa = EMPRESAS.find(e => e.id === r.empresa_id);
                const base = r.horas_previstas > 0 ? (r.presupuesto_ventas / r.horas_previstas).toFixed(2) : "—";
                return (
                  <tr key={r.id}>
                    <td style={S.td}>{empresa?.nombre || r.empresa_id}</td>
                    <td style={S.td}>{r.ejercicio}</td>
                    <td style={S.td}>{Number(r.presupuesto_ventas).toLocaleString("es-ES", { style: "currency", currency: "EUR" })}</td>
                    <td style={S.td}>{Number(r.horas_previstas).toLocaleString("es-ES")}</td>
                    <td style={S.td}><strong>{base} €/h</strong></td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button style={{ ...S.btnGhost, marginRight: 6, padding: "4px 10px", fontSize: 12 }} onClick={() => handleEdit(r)}>Editar</button>
                      <button style={S.btnDanger} onClick={() => handleDelete(r.id)}>Eliminar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
};

/* ═══════════════════════════════════════════
   TAB: CATEGORÍAS
   ═══════════════════════════════════════════ */
const TabCategorias = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [form, setForm] = useState({ nombre: "", coeficiente: "", orden: "" });
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await getIvghCategorias()); } catch { setRows([]); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.nombre || form.coeficiente === "") return;
    setMsg(null);
    try {
      await saveIvghCategoria({
        id: editing || undefined,
        nombre: form.nombre,
        coeficiente: Number(form.coeficiente),
        orden: Number(form.orden) || 0,
      });
      setMsg({ ok: true, text: "Categoría guardada" });
      setForm({ nombre: "", coeficiente: "", orden: "" });
      setEditing(null);
      load();
    } catch { setMsg({ ok: false, text: "Error al guardar" }); }
  };

  const handleEdit = (r) => {
    setEditing(r.id);
    setForm({ nombre: r.nombre, coeficiente: r.coeficiente, orden: String(r.orden) });
  };

  return (
    <Card>
      <CardHead icon={I.Tag} title="Categorías profesionales" />
      <CardBody>
        <Msg msg={msg} />
        <div style={S.row}>
          <div style={S.field}>
            <label style={S.label}>Nombre</label>
            <input style={S.input} placeholder="Gestor estándar" value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} />
          </div>
          <div style={{ ...S.field, maxWidth: 120 }}>
            <label style={S.label}>Coeficiente</label>
            <input style={S.input} type="number" step="0.05" placeholder="1.00" value={form.coeficiente} onChange={e => setForm({ ...form, coeficiente: e.target.value })} />
          </div>
          <div style={{ ...S.field, maxWidth: 80 }}>
            <label style={S.label}>Orden</label>
            <input style={S.input} type="number" placeholder="1" value={form.orden} onChange={e => setForm({ ...form, orden: e.target.value })} />
          </div>
        </div>
        <div style={S.actions}>
          <button style={S.btnPrimary} onClick={handleSave}>{editing ? "Actualizar" : "Añadir"}</button>
          {editing && <button style={S.btnGhost} onClick={() => { setEditing(null); setForm({ nombre: "", coeficiente: "", orden: "" }); }}>Cancelar</button>}
        </div>

        {loading ? <div style={{ padding: 20, color: "var(--ce-fg-muted)", fontSize: 13 }}>Cargando...</div> : (
          <table style={{ ...S.table, marginTop: 24 }}>
            <thead>
              <tr>
                <th style={S.th}>Categoría</th>
                <th style={S.th}>Coeficiente</th>
                <th style={S.th}>Orden</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={S.td}>{r.nombre}</td>
                  <td style={S.td}><Badge tone={r.coeficiente >= 1.15 ? "pos" : r.coeficiente >= 1.0 ? "neu" : "warn"}>{r.coeficiente}×</Badge></td>
                  <td style={S.td}>{r.orden}</td>
                  <td style={{ ...S.td, textAlign: "right" }}>
                    <button style={{ ...S.btnGhost, padding: "4px 10px", fontSize: 12 }} onClick={() => handleEdit(r)}>Editar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
};

/* ═══════════════════════════════════════════
   TAB: EMPLEADOS (Kronaly + ERP mapeo manual)
   ═══════════════════════════════════════════ */
const TabEmpleados = () => {
  const [empresa, setEmpresa] = useState("13");
  const [kronalyData, setKronalyData] = useState(null);
  const [vinculados, setVinculados] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [usuariosErp, setUsuariosErp] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // Estado por fila: { [kronaly_id]: { usuario_id, categoria_id, es_head } }
  const [vincForm, setVincForm] = useState({});

  const loadBase = useCallback(async () => {
    try {
      const [emp, cats, uErp] = await Promise.all([getIvghEmpleados(), getIvghCategorias(), getUsuariosErp()]);
      setVinculados(emp);
      setCategorias(cats);
      setUsuariosErp(uErp);
    } catch { /* */ }
  }, []);

  const loadKronaly = useCallback(async (empId) => {
    setLoading(true);
    setMsg(null);
    try {
      const data = await getEmpleadosKronaly(empId);
      setKronalyData(data);
      // Pre-fill auto-match por email
      if (data?.employees) {
        setVincForm(prev => {
          const next = { ...prev };
          for (const ke of data.employees) {
            if (!ke.ya_vinculado && ke.erp_match && !next[ke.kronaly_id]?.usuario_id) {
              next[ke.kronaly_id] = {
                ...next[ke.kronaly_id],
                usuario_id: String(ke.erp_match.usuario_id),
              };
            }
          }
          return next;
        });
      }
    } catch {
      setKronalyData(null);
      setMsg({ ok: false, text: "Error al cargar empleados de Kronaly" });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadKronaly(empresa); }, [empresa, loadKronaly]);

  const vinculadosUsuarioIds = new Set(vinculados.map(v => v.usuario_id));

  const handleVincular = async (ke) => {
    const opts = vincForm[ke.kronaly_id] || {};
    if (!opts.usuario_id) {
      setMsg({ ok: false, text: `Selecciona un usuario del ERP para ${ke.nombre}` });
      return;
    }
    if (!opts.categoria_id) {
      setMsg({ ok: false, text: `Selecciona una categoría para ${ke.nombre}` });
      return;
    }
    setMsg(null);
    try {
      await vincularEmpleado({
        usuario_id: Number(opts.usuario_id),
        kronaly_employee_id: ke.kronaly_id,
        categoria_id: Number(opts.categoria_id),
        es_head_of_operations: !!opts.es_head,
      });
      setMsg({ ok: true, text: `${ke.nombre} vinculado correctamente` });
      loadKronaly(empresa);
      loadBase();
    } catch { setMsg({ ok: false, text: "Error al vincular" }); }
  };

  const handleToggle = async (empId) => {
    try {
      await toggleIvghEmpleado(empId);
      loadBase();
    } catch { setMsg({ ok: false, text: "Error al cambiar estado" }); }
  };

  const handleDesvincular = async (empId) => {
    if (!window.confirm("¿Desvincular este empleado del módulo IVGH?")) return;
    try {
      await deleteIvghEmpleado(empId);
      loadKronaly(empresa);
      loadBase();
      setMsg({ ok: true, text: "Empleado desvinculado" });
    } catch { setMsg({ ok: false, text: "Error al desvincular" }); }
  };

  const updateVincForm = (kronalyId, field, value) => {
    setVincForm(prev => ({
      ...prev,
      [kronalyId]: { ...prev[kronalyId], [field]: value },
    }));
  };

  const pendientes = kronalyData?.employees?.filter(e => !e.ya_vinculado && e.status === "active") || [];

  return (
    <>
      {/* Selector de empresa */}
      <div style={{ marginBottom: 20 }}>
        <div style={S.row}>
          <div style={{ ...S.field, maxWidth: 250 }}>
            <label style={S.label}>Empresa (Kronaly)</label>
            <select style={S.select} value={empresa} onChange={e => { setEmpresa(e.target.value); setVincForm({}); }}>
              {EMPRESAS.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
            </select>
          </div>
        </div>
      </div>

      <Msg msg={msg} />

      {/* Empleados ya vinculados */}
      <Card style={{ marginBottom: 20 }}>
        <CardHead icon={I.Check} title="Empleados vinculados" />
        <CardBody>
          {vinculados.length === 0 ? (
            <Empty icon={I.Users} title="Sin empleados vinculados" sub="Vincula empleados desde la lista de Kronaly de abajo" />
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Activo</th>
                  <th style={S.th}>Empleado (ERP)</th>
                  <th style={S.th}>Email ERP</th>
                  <th style={S.th}>Categoría</th>
                  <th style={S.th}>Coef.</th>
                  <th style={S.th}>Kronaly ID</th>
                  <th style={S.th}>Rol</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {vinculados.map(r => (
                  <tr key={r.id} style={r.activo ? {} : { opacity: 0.5 }}>
                    <td style={S.td}>
                      <input type="checkbox" checked={!!r.activo} onChange={() => handleToggle(r.id)} title={r.activo ? "Desactivar" : "Activar"} />
                    </td>
                    <td style={S.td}>{r.usuario_nombre}</td>
                    <td style={S.td}><span style={{ fontSize: 12, color: "var(--ce-fg-muted)" }}>{r.usuario_email || "—"}</span></td>
                    <td style={S.td}>{r.categoria_nombre}</td>
                    <td style={S.td}><Badge tone="neu">{r.coeficiente}×</Badge></td>
                    <td style={S.td}><span style={{ fontFamily: "var(--ce-font-mono)", fontSize: 11 }}>{r.kronaly_employee_id ? r.kronaly_employee_id.slice(0, 8) + "…" : "—"}</span></td>
                    <td style={S.td}>{r.es_head_of_operations ? <Badge tone="info">Head of Ops</Badge> : "Gestor"}</td>
                    <td style={{ ...S.td, textAlign: "right" }}>
                      <button style={S.btnDanger} onClick={() => handleDesvincular(r.id)}>Desvincular</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {/* Empleados de Kronaly pendientes de vincular */}
      <Card>
        <CardHead icon={I.UserPlus} title={`Empleados en Kronaly — ${EMPRESAS.find(e => e.id === Number(empresa))?.nombre || empresa}`} />
        <CardBody>
          {loading ? (
            <div style={{ padding: 20, color: "var(--ce-fg-muted)", fontSize: 13 }}>Cargando empleados de Kronaly...</div>
          ) : kronalyData && !kronalyData.configured ? (
            <Empty icon={I.Plug} title="Kronaly no configurado" sub="No hay API key para esta empresa. Añade KRONALY_API_KEY_... en las variables de entorno." />
          ) : pendientes.length === 0 ? (
            <Empty icon={I.CheckCheck} title="Todos vinculados" sub="Todos los empleados activos de Kronaly ya están vinculados al módulo IVGH" />
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Empleado (Kronaly)</th>
                  <th style={S.th}>Email Kronaly</th>
                  <th style={S.th}>Cargo</th>
                  <th style={S.th}>Equipo</th>
                  <th style={S.th}>Usuario ERP</th>
                  <th style={S.th}>Categoría</th>
                  <th style={S.th}>Head of Ops</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {pendientes.map(ke => {
                  const opts = vincForm[ke.kronaly_id] || {};
                  return (
                    <tr key={ke.kronaly_id}>
                      <td style={S.td}>{ke.nombre}</td>
                      <td style={S.td}><span style={{ fontSize: 12 }}>{ke.email || "—"}</span></td>
                      <td style={S.td}><span style={{ fontSize: 12, color: "var(--ce-fg-muted)" }}>{ke.position || "—"}</span></td>
                      <td style={S.td}><span style={{ fontSize: 12 }}>{ke.teams?.map(t => t.name).join(", ") || "—"}</span></td>
                      <td style={S.td}>
                        <select
                          style={{ ...S.select, padding: "4px 8px", fontSize: 12, minWidth: 180 }}
                          value={opts.usuario_id || ""}
                          onChange={e => updateVincForm(ke.kronaly_id, "usuario_id", e.target.value)}
                        >
                          <option value="">— Seleccionar usuario —</option>
                          {usuariosErp
                            .filter(u => !vinculadosUsuarioIds.has(u.id) || String(u.id) === opts.usuario_id)
                            .map(u => (
                              <option key={u.id} value={u.id}>
                                {u.nombres} {u.apellidos || ""} ({u.email || "sin email"})
                              </option>
                            ))}
                        </select>
                        {ke.erp_match && opts.usuario_id === String(ke.erp_match.usuario_id) && (
                          <div style={{ fontSize: 10, color: "var(--ce-fg-success, #060)", marginTop: 2 }}>Auto-match por email</div>
                        )}
                      </td>
                      <td style={S.td}>
                        <select
                          style={{ ...S.select, padding: "4px 8px", fontSize: 12, minWidth: 130 }}
                          value={opts.categoria_id || ""}
                          onChange={e => updateVincForm(ke.kronaly_id, "categoria_id", e.target.value)}
                        >
                          <option value="">— Categoría —</option>
                          {categorias.map(c => <option key={c.id} value={c.id}>{c.nombre} ({c.coeficiente}×)</option>)}
                        </select>
                      </td>
                      <td style={S.td}>
                        <input
                          type="checkbox"
                          checked={!!opts.es_head}
                          onChange={e => updateVincForm(ke.kronaly_id, "es_head", e.target.checked)}
                        />
                      </td>
                      <td style={{ ...S.td, textAlign: "right" }}>
                        <button style={S.btnSuccess} onClick={() => handleVincular(ke)}>Vincular</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </>
  );
};

/* ═══════════════════════════════════════════
   PÁGINA PRINCIPAL
   ═══════════════════════════════════════════ */
const IvghConfig = () => {
  const [activeTab, setActiveTab] = useState("objetivos");

  return (
    <React.Fragment>
      <div style={S.page}>
        <div style={S.header}>
          <h1 style={S.title}>
            <I.BarChart3 size={24} />
            Configuración IVGH
          </h1>
          <div style={S.sub}>
            Importe por Valor y Gestión Horaria — configuración de objetivos, categorías y empleados
          </div>
        </div>

        <div style={S.tabs}>
          {tabs.map(t => (
            <button key={t.id} style={S.tab(activeTab === t.id)} onClick={() => setActiveTab(t.id)}>
              <t.icon size={14} />
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "objetivos" && <TabObjetivos />}
        {activeTab === "categorias" && <TabCategorias />}
        {activeTab === "empleados" && <TabEmpleados />}
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default IvghConfig;
